import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth0User } from '../context/Auth0UserContext';
import { useUser } from '../context/UserContext';
import { Box, Typography, Paper } from '@mui/material';
import { AdminPanelSettings } from '@mui/icons-material';

// Simple switch between Auth0 and original authentication
const useAuth0 = process.env.REACT_APP_USE_AUTH0 === 'true';

// Check for test user in localStorage — MUST also check env var to prevent
// browser console injection attacks (sessionStorage alone is not sufficient)
const testLoginEnabled = process.env.REACT_APP_ENABLE_TEST_LOGIN === 'true';
function getTestUser(): any | null {
  if (!testLoginEnabled) return null;
  if (sessionStorage.getItem('testLoginActive') !== 'true') return null;
  try {
    const stored = localStorage.getItem('currentUser');
    if (stored) {
      const user = JSON.parse(stored);
      // Only accept test users with role 'admin'
      if (user && user.role === 'admin') {
        console.log('🔐 [TEST MODE] Using test admin user from localStorage');
        return user;
      }
    }
  } catch (e) {
    console.error('Failed to parse test user:', e);
  }
  return null;
}

// Auth0 Admin Protected Route
function Auth0AdminProtectedRoute() {
  const { user: auth0User, loading } = useAuth0User();
  
  // In test mode, check for test user first
  const testUser = getTestUser();
  if (testUser) {
    return <AdminProtectedRouteComponent user={testUser} loading={false} />;
  }
  
  return <AdminProtectedRouteComponent user={auth0User} loading={loading} />;
}

// Original Admin Protected Route
function OriginalAdminProtectedRoute() {
  const { user } = useUser();
  
  // In test mode, check for test user first
  const testUser = getTestUser();
  if (testUser) {
    return <AdminProtectedRouteComponent user={testUser} loading={false} />;
  }
  
  return <AdminProtectedRouteComponent user={user} loading={false} />;
}

// Main Admin Protected Route - Renders appropriate authentication wrapper
const AdminProtectedRoute: React.FC = () => {
  return useAuth0 ? <Auth0AdminProtectedRoute /> : <OriginalAdminProtectedRoute />;
};

// Shared Admin Protected Route Component
function AdminProtectedRouteComponent({ user, loading }: { user: any, loading: boolean }) {

  // Show loading while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Checking admin access...
        </Typography>
      </Box>
    );
  }

  // Redirect to login/home if not authenticated
  if (!user) {
    // Auth0 mode: redirect to home (no /login page)
    // Original mode: redirect to /login
    return <Navigate to={useAuth0 ? "/" : "/login"} replace />;
  }

  // Check if user has admin role
  if (user.role !== 'admin') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          p: 3
        }}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400,
            borderRadius: 2
          }}
        >
          <AdminPanelSettings
            sx={{
              fontSize: 48,
              color: 'error.main',
              mb: 2
            }}
          />
          <Typography variant="h5" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You need admin privileges to access this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // User is authenticated and has admin role - render the protected content
  return <Outlet />;
};

export default AdminProtectedRoute;
